import { Box, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone'; // Importez useDropzone

const DragAndDropFile = ({ handleAttachmentFiles, size, handleSizeError }) => {
  const convertSizeToMo = (attachmentsSize) => {
    return (attachmentsSize / (1024 * 1024)).toFixed(2);
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      // acceptedFiles est un tableau d'objets File.
      // Votre logique actuelle attend un tableau avec un seul fichier.
      // Si vous voulez permettre plusieurs fichiers, adaptez handleAttachmentFiles.
      // Pour l'instant, je gère le cas 1 fichier comme avant, mais c'est une différence.
      if (acceptedFiles.length > 0) {
        // Appelez handleAttachmentFiles avec les fichiers acceptés
        handleAttachmentFiles(acceptedFiles); // Passer tous les fichiers acceptés
      }
    },
    [handleAttachmentFiles] // Assurez-vous d'inclure handleAttachmentFiles dans les dépendances
  );

  // Définition des options de dropzone
  const maxFileSize = 5 * 1024 * 1024; // 5 Mo en octets (votre commentaire disait 5 Mo max par fichier)
  // Note: react-drag-drop-files utilisait 40 Mo comme 'maxSize' mais le label disait 5 Mo.
  // J'ai pris 5 Mo pour la logique de validation. Si 40 Mo était la taille réelle, ajustez.

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      maxSize: maxFileSize, // Taille maximale acceptée pour un seul fichier
      // Vous pouvez aussi ajouter 'accept' pour limiter les types de fichiers :
      // accept: {
      //   'image/*': ['.jpeg', '.png', '.gif'],
      //   'application/pdf': ['.pdf'],
      // },
      // Si vous voulez gérer les fichiers non acceptés (trop gros, mauvais type, etc.)
      // La propriété 'noClick' peut être utile si vous voulez que seul le drag-drop fonctionne
      // ou 'noKeyboard' pour désactiver l'accessibilité clavier.
    });

  // Gestion des erreurs de taille (similaire à onSizeError)
  React.useEffect(() => {
    if (fileRejections.length > 0) {
      fileRejections.forEach((rejection) => {
        if (rejection.errors.some((error) => error.code === 'file-too-large')) {
          handleSizeError(); // Appeler votre fonction d'erreur de taille
        }
        // Vous pouvez ajouter d'autres logiques pour d'autres types d'erreurs (type de fichier, etc.)
        // console.log("Rejection errors:", rejection.errors);
      });
    }
  }, [fileRejections, handleSizeError]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 1,
      }}
    >
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400', // Change la couleur de la bordure au drag
          borderRadius: '4px',
          padding: '20px',
          textAlign: 'center',
          cursor: 'pointer',
          flexGrow: 1, // Prend l'espace disponible
          mr: 2, // Marge à droite pour séparer du texte de taille
          // Styles pour le texte à l'intérieur
          '& p': {
            color: 'text.secondary',
          },
          // Styles pour l'état de glissement actif
          ...(isDragActive && {
            backgroundColor: 'action.hover', // Légère couleur de fond
          }),
        }}
      >
        <input {...getInputProps()} />
        {/* Affichage du libellé basé sur l'état de glissement */}
        {isDragActive ? (
          <Typography variant="body2">Déposez ici !</Typography>
        ) : (
          <Typography variant="body2">
            Déposez vos fichiers ou cliquez pour les télécharger (5 Mo max par
            fichier)
          </Typography>
        )}
      </Box>

      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        Taille totale actuelle : <b>{convertSizeToMo(size) || 0} Mo</b>
      </Typography>
    </Box>
  );
};

export default DragAndDropFile;
